function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else obj[key] = value;
    return obj;
}
/**
 * PositionManager merges two position layers:
 *
 *   saved (persisted, post-fromStorage) > sentinel
 *
 * It stays pure (no React). A surface uses it by:
 *   - seeding `saved` from the backend's NodePositionMap
 *   - calling `commitDrags(updates, ctx)` on react-flow's drag-stop event
 *   - subscribing via `onDirty` to batch-persist saved positions after a drag commit
 *
 * Coordinate contract:
 *   - `saved` holds RF-native (parent-local) positions *after* any
 *     fromStorage normalization. The manager never calls fromStorage internally.
 *     Callers convert once on seed and hand us RF-native positions.
 *   - `resolve()` returns RF-native positions directly usable by ReactFlow.
 *   - `commitDrags()` invokes `storage.toStorage` (if provided) before emitting
 *     the persisted value to `onDirty`.
 */ export class PositionManager {
    /** Replace the saved layer entirely (e.g., after a persist completes). */ setSaved(map) {
        this.saved = new Map(map);
    }
    /** Resolve a node's effective RF position. */ resolve(node) {
        return this.saved.get(node.id) ?? this.sentinelFn(node, this.getSavedForSentinel);
    }
    /** True when a node has a saved position. */ hasSaved(id) {
        return this.saved.has(id);
    }
    /**
   * Batched drag commit: persist multiple final RF positions into `saved` and
   * emit a single onDirty callback with all storage-normalized values. Runs on
   * every react-flow drag stop, single node or group — emitting once lets the
   * consumer collapse the whole move into a single undo entry.
   */ commitDrags(updates, ctx) {
        if (updates.length === 0) return;
        const emit = [];
        for (const u of updates){
            this.saved.set(u.id, u.position);
            if (this.onDirty) {
                const persisted = this.storage ? this.storage.toStorage(u.id, u.position, ctx) : u.position;
                emit.push({
                    id: u.id,
                    position: persisted
                });
            }
        }
        if (emit.length) this.onDirty?.(emit);
    }
    /**
   * Persist a set of positions that the caller has just finalized (e.g. from
   * an ELK-on-mount seeding pass). Populates `saved`, emits onDirty.
   */ seedSaved(updates, ctx) {
        if (updates.length === 0) return;
        const emit = [];
        for (const u of updates){
            this.saved.set(u.id, u.position);
            const persisted = this.storage ? this.storage.toStorage(u.id, u.position, ctx) : u.position;
            emit.push({
                id: u.id,
                position: persisted
            });
        }
        this.onDirty?.(emit);
    }
    constructor(opts = {}){
        _define_property(this, "saved", void 0);
        _define_property(this, "storage", void 0);
        _define_property(this, "sentinelFn", void 0);
        _define_property(this, "getSavedForSentinel", void 0);
        _define_property(this, "onDirty", void 0);
        this.saved = new Map(opts.initialSaved ?? new Map());
        this.storage = opts.storage;
        this.sentinelFn = opts.sentinel ?? (()=>({
                x: 0,
                y: 0
            }));
        this.getSavedForSentinel = (id)=>this.saved.get(id) ?? null;
        this.onDirty = opts.onDirty;
    }
}

//# sourceMappingURL=PositionManager.js.map